import React, { useEffect, useMemo } from 'react';
import ReactDataTable from 'react-data-table-component';
import styled from 'styled-components';

import { Loading } from '@/shared/presentation/components/ui/Loading';

import { useDebounce } from '../../utils/debounce';

const TableContainer = styled.div`
  position: relative;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-bottom: 6px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
`;

const FilterInput = styled.input`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px;
  max-width: 300px;
  width: 100%;
  font-size: 14px;
  color: #636769;
  &:focus {
    outline: none;
  }
`;

const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: var(--gray-40);
`;

type Column = {
  name: string;
  selector?: (row: any) => any;
  sortable?: boolean;
  filterable?: boolean;
};

type Props = {
  columns: Column[];
  data: any[];
  actions?: { name: string | React.ReactNode; onClick: (row: any) => void }[];
  headerActions?: React.ReactNode;
  isLoading?: boolean;
  selectableRows?: boolean;
  selectableRowsSingle?: boolean;
  paginationServer?: boolean;
  progressPending?: boolean;
  paginationTotalRows?: number;
  searchPlaceholder?: string;
  onSearch?: (search: string) => void;
  onSelectedRowsChange?: (rows: any[]) => void;
  onChangeRowsPerPage?: (pageSize: number, page: number) => void;
  onChangePage?: (page: number) => void;
};

const ActionContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  width: 100%;
`;

const TotalContainer = styled.div`
  position: absolute;
  left: 0;
  bottom: 20px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--gray-90);
  cursor: pointer;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;
  width: 100%;
  text-align: left;
`;

const customStyles = {
  headCells: {
    style: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#666666',
      paddingLeft: '0',
    },
  },
  cells: {
    style: {
      fontSize: '14px',
      color: '#636769',
      paddingLeft: '0',
    },
  },
};

const paginationComponentOptions = {
  rowsPerPageText: 'Itens por página',
  rangeSeparatorText: 'de',
  selectAllRowsItem: true,
  selectAllRowsItemText: 'Todos',
};

export const DataTable: React.FC<Props> = (props) => {
  const actions = useMemo(() => props.actions || [], [props.actions]);
  const actionsColumn = useMemo(
    () =>
      actions.length > 0
        ? {
            name: '',
            button: true,
            cell: (row) => (
              <ActionContainer>
                {actions.map((action, index) => (
                  <ActionButton key={index} onClick={() => action.onClick(row)}>
                    {action.name}
                  </ActionButton>
                ))}
              </ActionContainer>
            ),
          }
        : {},
    [actions],
  );

  const columns = useMemo(() => [...props.columns, actionsColumn], [props.columns, actionsColumn]);
  const [filterText, setFilterText] = React.useState(() => '');

  const debouncedValue = useDebounce(filterText, 500);

  useEffect(() => {
    if (props.onSearch) {
      props.onSearch(debouncedValue);
    }
  }, [debouncedValue]);

  const filteredData = useMemo(() => {
    if (filterText === '') {
      return props.data;
    }

    if (props.onSearch) {
      return props.data;
    }

    return props.data.filter((item) => {
      const values = props.columns.map((column) => column.selector(item));
      return values.some((value) =>
        value.toString().toLowerCase().includes(filterText.toLowerCase()),
      );
    });
  }, [filterText, props.data]);

  const handleChange = ({ selectedRows }) => {
    if (props.onSelectedRowsChange) {
      props.onSelectedRowsChange(selectedRows);
    }
  };

  return (
    <TableContainer>
      <TableHeader>
        <FilterInput
          type="text"
          placeholder={props.searchPlaceholder || 'Pesquisar'}
          disabled={props.isLoading}
          onChange={(e) => setFilterText(e.target.value)}
          value={filterText}
        />
        {props.headerActions && <div>{props.headerActions}</div>}
      </TableHeader>
      {props.isLoading ? (
        <LoadingContainer>
          <Loading />
        </LoadingContainer>
      ) : (
        <ReactDataTable
          selectableRows={props.selectableRows}
          onSelectedRowsChange={handleChange}
          selectableRowsSingle={props.selectableRowsSingle}
          columns={columns}
          data={filteredData}
          paginationComponentOptions={paginationComponentOptions}
          customStyles={customStyles}
          noDataComponent={<NoDataContainer>Nenhum registro encontrado</NoDataContainer>}
          pagination
          paginationTotalRows={props.paginationTotalRows}
          paginationServer={props.paginationServer}
          onChangeRowsPerPage={props.onChangeRowsPerPage}
          onChangePage={props.onChangePage}
        />
      )}
      <TotalContainer>
        Total de itens: {props.paginationServer ? props.paginationTotalRows : props.data.length}
      </TotalContainer>
    </TableContainer>
  );
};
